import {DataTable, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, DataTableSkeleton, DataTableHeader} from '@carbon/react';
import {useContext, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {NetworkContext} from '../lib/context';
import ComplianceIndicator from './compliance-indicator';
import {Edit, Checkmark} from '@carbon/icons-react';
import getFormatter from '../../../lib/formatter';
import ErrorDisplay from './error-display';

interface ComplianceSummaryTableProps {
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    exportButton?: React.ReactNode
}

const ComplianceSummaryTable = observer(({specialtyLoading, specialtyError, exportButton}: ComplianceSummaryTableProps) => {
    const networkOptimizationStore = useContext(NetworkContext);
    const loading = useMemo(() => specialtyLoading ?? false, [specialtyLoading]);
    const error = useMemo(() => specialtyError ?? null, [specialtyError]);
    const {state, county} = networkOptimizationStore.appliedGeographyFilter;
    const [headers, setHeaders] = useState<Array<DataTableHeader>>([]);
    const [rows, setRows] = useState<Array<{id:string, specialtyName?: string, specialtyCode?: string, passed?: boolean, cells: Array<{id:string, value:any, width?: string, type?: string}>}>>([]);

    const DEFAULT_COMPLIANCE_TARGET = 90;

    function renderSpecialtyName(score: {name: string, revision_id: boolean}) {
        if (networkOptimizationStore.revisionId > 0 && score.revision_id) {
            return (
                <div className="flex space-x-3 items-center" title='Click to filter by this specialty'>
                    <span className="block">{score.name}</span>
                    <Edit className="block" />
                </div>
            );
        }
        return <span title='Click to filter by this specialty'>{score.name}</span>;
    }

    useEffect(() => {
        if (state && county) {
            setHeaders([
                {key: 'specialty', header: 'Specialty'},
                {key: 'passFail', header: 'Pass/Fail'},
                {key: 'minRequired', header: 'Min Required'},
                {key: 'numProviders', header: '# of Distinct Providers'},
                {key: 'accessTime', header: '% with Access (Time)'},
                {key: 'accessDistance', header: '% with Access (Distance)'},
                {key: 'complianceScore', header: 'Compliance Target'},
            ]);
        } else if(state && !county) {
            //County level summary.
            setHeaders([ 
                {key: 'county', header: 'County'},
                {key: 'passFail', header: 'Pass/Fail'},
                {key: 'passFailRatio', header: 'Pass/Fail Ratio'},
            ]);
        } else if(!state && !county) {
            //State level summary.
            setHeaders([ 
                {key: 'state', header: 'State'},
                {key: 'passFail', header: 'County Pass/Fail'},
                {key: 'passFailRatio', header: 'County Pass/Fail Ratio'},
            ]);
        }
    }, [state, county]);

    useEffect(() => {
        if (networkOptimizationStore.loadingSpecialtyData) return;
        if (state && county) {
            setRows(complianceScores
                .filter(score => Array.isArray(score.scores))
                .map((score, index) => ({
                    id: index.toString(),
                    specialtyName: score.name,
                    specialtyCode: score.specialtyCode,
                    passed: score.compliance,
                    cells: [
                        {id: 'specialty', value: renderSpecialtyName(score), width: '40%'},
                        {id: 'passFail', value: <ComplianceIndicator passed={score.compliance} showText />, width: '10%'},
                        {id: 'minRequired', value: score.scores?.[4]?.value ?? '-', width: '10%'},
                        {id: 'numProviders', value: score.scores?.[2]?.value ?? '-', width: '10%'},
                        {id: 'accessTime', value: score.scores?.[0]?.value ?? 0, width: '10%'},
                        {id: 'accessDistance', value: score.scores?.[1]?.value ?? 0, width: '10%'},
                        {id: 'complianceScore', value: `${score.compliance_target / 100}`, type: 'percent', width: '10%'},
                    ],
                }))
            );
        } else if (state && !county) {
            //County level summary.
            setRows(complianceScores.map((score, index) => ({
                id: index.toString(),
                cells: [
                    {id: 'county', value: score.county, width: '40%'},
                    {id: 'passFail', value: <ComplianceIndicator passed={score.pass_fail_flag === '0' ? false : true} showText />, width: '30%'},
                    {id: 'passFailRatio', value: score.pass_fail_ratio || '-', width: '30%'}
                ],
            })));
        } else if (!state && !county) {
            //State level summary.
            setRows(complianceScores.map((score, index) => ({
                id: index.toString(),
                cells: [
                    {id: 'state', value: score.state, width: '40%'},
                    {id: 'passFail', value: <ComplianceIndicator passed={score.pass_fail_flag === '0' ? false : true} showText />, width: '30%'},
                    {id: 'passFailRatio', value: score.pass_fail_ratio || '-', width: '30%'}
                ],
            })));
        }   
    }, [networkOptimizationStore.complianceScores, networkOptimizationStore.loadingSpecialtyData, state, county]);

    const {complianceScores} = networkOptimizationStore;

    const getCellClassName = (cellId: string, cellValue: any, passed: boolean, minRequired: number, complianceTarget: number) => {
        if (['accessTime', 'accessDistance'].includes(cellId)) {
            return cellValue / 100 < complianceTarget ? 'bg-yellow-200' : 'bg-green-200';
        }

        if (['complianceScore'].includes(cellId)) {
            return !passed ? 'bg-yellow-200' : 'bg-green-200';
        }

        if (cellId === 'numProviders') {
            return cellValue >= minRequired ? 'bg-green-200' : 'bg-yellow-200';
        }
        return '';
    };

    const selectedCodes = useMemo(
        () => networkOptimizationStore.specialtyFilter
            ? networkOptimizationStore.specialtyFilter.split('|').filter(Boolean)
            : [],
        [networkOptimizationStore.specialtyFilter]
    );

    const renderedRows = useMemo(() => rows.map(row => {
        const isSelected = row.specialtyCode ? selectedCodes.includes(row.specialtyCode) : false;
        const isClickable = !!row.specialtyCode;
        const handleToggle = isClickable ? () => {
            networkOptimizationStore.toggleSpecialtyFilter(row.specialtyCode!);
        } : undefined;
        return (
            <TableRow
                key={row.id}
                className={`table w-full ${isClickable ? 'cursor-pointer hover:bg-blue-50' : ''} ${isSelected ? 'border-l-4 border-blue-600 bg-blue-50' : ''}`}
                onClick={handleToggle}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle!(); } } : undefined}
            >
                {row.cells.map((cell) => {
                    const minRequiredCell = row.cells.find(c => c.id === 'minRequired');
                    const minRequired = minRequiredCell ? parseFloat(minRequiredCell.value) || 0 : 0;
                    const complianceTarget = Number(row.cells.find(c => c.id === 'complianceScore')?.value) || DEFAULT_COMPLIANCE_TARGET;
                    const formatter = cell?.type ? getFormatter(cell.type) : {format: (value: any) => value};
                    const cellContent = cell.id === 'specialty' && isSelected
                        ? <div className="flex items-center gap-2"><Checkmark size={16} className="text-blue-600 shrink-0" />{cell.value}</div>
                        : formatter.format(cell.value);
                    return (
                        <TableCell
                            key={cell.id}
                            className={`p-2 ${getCellClassName(cell.id, parseFloat(cell.value), row.passed ?? false, minRequired, complianceTarget)}`}
                            style={{width: cell.width}}
                        >
                            {cellContent}
                        </TableCell>
                    );
                })}
            </TableRow>
        );
    }), [rows, selectedCodes, networkOptimizationStore]);

    if (!networkOptimizationStore.readyToLoadData()) {
        return null;
    }

    if (error) {
        return <ErrorDisplay error={error} title='Unable to Load Compliance Summary' />;
    }

    return (
        <div className="compliance-summary-page bg-white p-4 w-full">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-baseline gap-4">
                    <h3 className="text-xl font-bold">Estimated Compliance Summary</h3>
                    {state && county && networkOptimizationStore.specialtyFilter && (
                        <span className="text-sm text-gray-500">
                            {networkOptimizationStore.specialtyFilter.split('|').filter(Boolean).length} selected
                            {' · '}
                            <button
                                className="text-blue-600 underline hover:text-blue-800"
                                onClick={() => networkOptimizationStore.setSpecialtyFilter('')}
                            >
                                Clear
                            </button>
                        </span>
                    )}
                </div>
                {exportButton}
            </div>
            {loading ? <DataTableSkeleton 
                headers={headers} 
                columnCount={headers.length}
                rowCount={12}
                showHeader={false}
                showToolbar={false}
            /> : <TableContainer className="max-h-[70vh]" data-testid="estimated-compliance-summary-table-container">
                <DataTable rows={rows} headers={headers}>
                    {({headers}) => (
                        <Table className="min-w-full">
                            <TableHead>
                                <TableRow className="sticky table top-0 bg-white z-10">
                                    {headers.map(header => (
                                        <TableHeader
                                            key={header.key}
                                            className={`bg-white z-20 border-b border-gray-300 text-left p-2`}
                                            style={{width: `${header.key === 'specialty' || header.key === 'state' || header.key === 'county' ? '40%' : `${60 / (headers.length - 1)}%`}`}}
                                        >
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody className="block max-h-[60vh] overflow-y-auto">
                                {rows.length === 0 ? (
                                    <TableRow className="table w-full">
                                        <TableCell
                                            colSpan={headers.length}
                                            className="p-3"
                                            style={{textAlign: 'center', fontSize: '1rem', fontWeight: '500'}}
                                        >
                                            No Data Available
                                        </TableCell>
                                    </TableRow>
                                ) : renderedRows}
                            </TableBody>
                        </Table>
                    )}
                </DataTable>
            </TableContainer>}
        </div>
    );
});

export default ComplianceSummaryTable;
